import { Entitlements } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useGetRoutes } from '@experiences/util';
import React, {
    useEffect,
    useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { type RouteObject } from 'react-router';

import * as RouteNames from '../../common/constants/RouteNames';
import useCanAccessUsageInsights from '../../common/hooks/useCanAccessUsageInsights';
import useCheckLicense from '../../common/hooks/useCheckLicense';
import useIsOnPrem from '../../common/hooks/useIsOnPrem';
import InvalidUrl from '../../component/authentication/InvalidUrl';
import { useEntitlements } from '../../component/entitlements/EntitlementsContextProvider';
import { GovernanceV2Routes } from '../../component/governanceV2/GovernanceV2Routes';
import { HomeRoutes } from '../../component/home/HomeRoutes';
import { HostRoutes } from '../../component/HostRoutes';
import NotificationPageComponent from '../../component/notifications/NotificationPageComponent';
import { OrgMigrationPage } from '../../component/organizationsettings/subcomponents/OrgMigration/OrgMigrationPage';
import { ProfileRoutes } from '../../component/profile/ProfileRoutes';
import { ResourceCenterRoutes } from '../../component/resourceCenter/ResourceCenterRoutes';
import { PrivateRootRoutes } from '../../component/RootRoutes';
import {
    canViewAdminSelector,
    isAdminSelector,
    isHostModeSelector,
} from '../../store/selectors';
import CheckGuard from '../helpers/CheckGuard';
import {
    OrganizationConsumptionDashboardRoute,
    TenantConsumptionDashboardRoute,
} from '../helpers/ConsumptionDashboardShellRoutes';
import { LicensesRouteAccessGuard } from '../helpers/LicensesRouteAccessGuard';
import { UserUsageRoute } from '../helpers/UserUsageShellRoutes';
import {
    flattenRedirectUrls,
    recursiveRouteReplace,
} from './routeHelpers';
import type { UiRouteObject } from './UiRouteObject';
import { useEcommerceRoutesDefault } from './useEcommerceRoutesDefault';
import { useEcommerceRoutesK8s } from './useEcommerceRoutesK8s';

const usePrivateShellRoutes = () => {
    const EnabledScheduledOrgMigration = useFeatureFlagValue(Features.EnabledScheduledOrgMigration.name);
    const EnableConsumptionDashboardView = useFeatureFlagValue(Features.EnableConsumptionDashboardView.name);
    const EnableConsumptionDashboardSap = useFeatureFlagValue(Features.EnableConsumptionDashboardSap.name);
    const EnableUserUsageView = useFeatureFlagValue(Features.EnableUserUsageView.name);

    const isHostMode = useSelector(isHostModeSelector);
    const isAdmin = useSelector(isAdminSelector);
    const canViewAdmin = useSelector(canViewAdminSelector);
    const isOnPrem = useIsOnPrem();
    const isGovernanceV2Enabled = useFeatureFlagValue(Features.EnabledPortalNewGovernanceUX.name);
    const { isFreeOrCommunity } = useCheckLicense();
    const canAccessUsageInsights = useCanAccessUsageInsights();
    const ecommerceRoutes = useEcommerceRoutesDefault();
    const ecommerceRoutesK8s = useEcommerceRoutesK8s();
    const { hasEntitlement } = useEntitlements();

    const { setRoutes } = useGetRoutes();

    const hasAdminAccess = isAdmin || canViewAdmin;
    const isSapOrganization = hasEntitlement(Entitlements.PlatformSapEnabled);

    const canAccessConsumptionDashboard = !isHostMode && EnableConsumptionDashboardView
        && canAccessUsageInsights
        && (!isSapOrganization || EnableConsumptionDashboardSap);

    const canAccessUserUsage = !isHostMode && EnableUserUsageView && canAccessUsageInsights;

    useEffect(() => {
        setRoutes([
            ProfileRoutes,
            ...(hasAdminAccess && !isHostMode ? [ PrivateRootRoutes ] : []),
            ...(isHostMode ? [ HostRoutes ] : [ HomeRoutes, ResourceCenterRoutes ]),
            ...(isGovernanceV2Enabled && hasAdminAccess && !isHostMode ? [ GovernanceV2Routes ] : []),
        ]);
    }, [ hasAdminAccess, isGovernanceV2Enabled, isHostMode, setRoutes ]);

    return useMemo(() => {
        const nestedRoutes = [
            ...(hasAdminAccess && !isHostMode ? [ PrivateRootRoutes ] : []),
            ...(isHostMode
                ? [ HostRoutes ]
                : [
                    HomeRoutes,
                    ResourceCenterRoutes,
                ]),
            ...(isGovernanceV2Enabled && hasAdminAccess && !isHostMode ? [ GovernanceV2Routes ] : []),
            ProfileRoutes,
            ...(canAccessConsumptionDashboard
                ? [
                    {
                        path: RouteNames.LicensingConsumptionDashboard,
                        element: (
                            <LicensesRouteAccessGuard>
                                <OrganizationConsumptionDashboardRoute />
                            </LicensesRouteAccessGuard>
                        ),
                    },
                    {
                        path: RouteNames.LicensingTenantConsumptionDashboard,
                        element: (
                            <LicensesRouteAccessGuard>
                                <TenantConsumptionDashboardRoute />
                            </LicensesRouteAccessGuard>
                        ),
                    },
                ]
                : []),
            ...(canAccessUserUsage
                ? [
                    {
                        path: RouteNames.LicensingUserUsage,
                        element: (
                            <LicensesRouteAccessGuard>
                                <UserUsageRoute />
                            </LicensesRouteAccessGuard>
                        ),
                    },
                ]
                : []),
            {
                path: RouteNames.Notifications,
                element: <NotificationPageComponent />,
            },
            {
                path: RouteNames.OrganizationMigration,
                element:
                CheckGuard(!isFreeOrCommunity() && EnabledScheduledOrgMigration, <OrgMigrationPage />),
            },
            ...(isAdmin ? (isOnPrem ? ecommerceRoutesK8s : ecommerceRoutes) : []),
            {
                path: '*', // Fallback for invalid urls
                element: <InvalidUrl />,
            },
        ] satisfies UiRouteObject[];

        const flattenedRoutes = flattenRedirectUrls(nestedRoutes);
        const fullRoutes = [
            ...nestedRoutes,
            ...flattenedRoutes,
        ] satisfies UiRouteObject[];
        const relativeRoutes = recursiveRouteReplace(fullRoutes, RouteNames.PortalPrefix);
        return relativeRoutes as RouteObject[];
    }, [
        EnabledScheduledOrgMigration,
        canAccessConsumptionDashboard,
        canAccessUserUsage,
        ecommerceRoutes,
        ecommerceRoutesK8s,
        hasAdminAccess,
        isAdmin,
        isFreeOrCommunity,
        isGovernanceV2Enabled,
        isHostMode,
        isOnPrem,
    ]);
};

export default usePrivateShellRoutes;
